import React, { useState, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import axios from "axios";
import LoginPage from "./pages/LoginPage";
import Dashboard from "./pages/Dashboard";
import SuperadminPanel from "./pages/SuperadminPanel";
import MarketingPanel from "./pages/MarketingPanel";
import MarketingPerformancePanel from "./pages/MarketingPerformancePanel";
import FollowUpPanel from "./pages/FollowUpPanel";
import ToDoListPanel from "./pages/ToDoListPanel";
import MemberReportPanel from "./pages/MemberReportPanel";
import PerformanceReportPanel from "./pages/PerformanceReportPanel";
import ExportLogsPanel from "./pages/ExportLogsPanel";
import AnalyticsDashboard from "./pages/AnalyticsDashboard";
import MemberHistoryLogPanel from "./pages/MemberHistoryLogPanel";
import AppLayout from "./components/AppLayout";
import { Toaster } from "./components/ui/sonner";

// Resolve backend URL:
// - On custom domain (e.g. tattoo-marketinghub.xyz): use same-origin (avoids CORS + ISP blocking).
// - On Emergent preview/deploy host or localhost: use the env var baked at build time.
const resolveBackendUrl = () => {
  const envUrl = process.env.REACT_APP_BACKEND_URL;
  if (typeof window !== "undefined") {
    const host = window.location.hostname;
    const isEmergentHost =
      host.endsWith(".emergentagent.com") ||
      host.endsWith(".emergent.host") ||
      host === "localhost" ||
      host === "127.0.0.1";
    if (!isEmergentHost) {
      return window.location.origin;
    }
  }
  return envUrl;
};

const BACKEND_URL = resolveBackendUrl();
export const API = `${BACKEND_URL}/api`;

export const axiosInstance = axios.create({
  baseURL: API,
});

// Request interceptor to add token
axiosInstance.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle 401
axiosInstance.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      window.location.href = "/login";
    }
    return Promise.reject(error);
  }
);

const ProtectedRoute = ({ children, allowedRoles }) => {
  const token = localStorage.getItem("token");
  const user = JSON.parse(localStorage.getItem("user") || "null");

  if (!token || !user) {
    return <Navigate to="/login" replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user.role)) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// Module-scope role tuples — stable across renders so <ProtectedRoute />
// receives the same `allowedRoles` reference each time, avoiding needless
// re-renders.
const ROLE_SUPERADMIN_ONLY = ["superadmin"];
const ROLE_MARKETING = ["marketing", "superadmin"];
const ROLE_FOLLOW_UP = ["follow_up", "superadmin"];
const ROLE_SUPERVISOR = ["supervisor", "superadmin"];

function App() {
  const [user, setUser] = useState(null);

  useEffect(() => {
    const storedUser = localStorage.getItem("user");
    if (storedUser) {
      setUser(JSON.parse(storedUser));
    }
  }, []);

  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<LoginPage setUser={setUser} />} />
          <Route
            element={
              <ProtectedRoute>
                <AppLayout user={user} />
              </ProtectedRoute>
            }
          >
            <Route path="/dashboard" element={<Dashboard user={user} />} />
            <Route
              path="/superadmin"
              element={
                <ProtectedRoute allowedRoles={ROLE_SUPERADMIN_ONLY}>
                  <SuperadminPanel user={user} />
                </ProtectedRoute>
              }
            />
            <Route
              path="/marketing"
              element={
                <ProtectedRoute allowedRoles={ROLE_MARKETING}>
                  <MarketingPanel user={user} />
                </ProtectedRoute>
              }
            />
            <Route
              path="/follow-up"
              element={
                <ProtectedRoute allowedRoles={ROLE_FOLLOW_UP}>
                  <FollowUpPanel user={user} />
                </ProtectedRoute>
              }
            />
            <Route
              path="/todo-list"
              element={
                <ProtectedRoute allowedRoles={ROLE_SUPERVISOR}>
                  <ToDoListPanel user={user} />
                </ProtectedRoute>
              }
            />
            <Route
              path="/member-report"
              element={
                <ProtectedRoute allowedRoles={ROLE_SUPERVISOR}>
                  <MemberReportPanel user={user} />
                </ProtectedRoute>
              }
            />
            <Route
              path="/performance-report"
              element={
                <ProtectedRoute allowedRoles={ROLE_SUPERVISOR}>
                  <PerformanceReportPanel user={user} />
                </ProtectedRoute>
              }
            />
            <Route
              path="/export-logs"
              element={
                <ProtectedRoute allowedRoles={ROLE_SUPERADMIN_ONLY}>
                  <ExportLogsPanel user={user} />
                </ProtectedRoute>
              }
            />
            <Route
              path="/marketing-performance"
              element={
                <ProtectedRoute allowedRoles={ROLE_MARKETING}>
                  <MarketingPerformancePanel user={user} />
                </ProtectedRoute>
              }
            />
            <Route path="/analytics" element={<AnalyticsDashboard user={user} />} />
            <Route
              path="/member-history"
              element={
                <ProtectedRoute allowedRoles={ROLE_SUPERVISOR}>
                  <MemberHistoryLogPanel user={user} />
                </ProtectedRoute>
              }
            />
          </Route>
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" richColors />
    </div>
  );
}

export default App;